import { useState, useMemo, useCallback, useRef } from "react";
import type { Appearance, Stripe } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { OrderQuantitySelector } from "./OrderQuantitySelector";
import { CheckoutTipSelector } from "./CheckoutTipSelector";
import { resolveCheckoutSubmitBtnText } from "@/lib/contactCustomFields";
import { getContrastTextColor, mutedOnSurfaceHex } from "@/utils/colorUtils";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";

interface Product {
  _id: string;
  name?: string;
  /** Display / line math uses integer cents */
  price: number;
  pricePendingVariant?: boolean;
  variants?: { _id: string; name: string }[];
  selectedVariantId?: string;
  variantSelectNeedsAttention?: boolean;
}

export type CheckoutPaymentCheckoutSettings = {
  tipping?: {
    enabled: boolean;
    amount1?: number;
    amount2?: number;
    amount3?: number;
  };
  colors?: {
    backgroundColor?: string;
    buttonColor?: string;
    accentColor?: string;
  };
} | null;

export type CheckoutSessionLineItemBody = {
  productId: string;
  quantity: number;
  variantId?: string;
};

interface CheckoutPaymentOrderAndTipsProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  onRemoveLine?: ({ displayIndex }: { displayIndex: number }) => void;
  onLineVariantChange?: ({
    displayIndex,
    variantId,
  }: {
    displayIndex: number;
    variantId: string;
  }) => void;
  total: number;
  shippingCents?: number;
  shippingLabel?: string;
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  orderBumpCents?: number;
  orderBumpSelectable?: boolean;
  orderBumpLineTotalCents?: number;
  orderBumpTitle?: string;
  orderBumpDescription?: string;
  orderBumpChecked?: boolean;
  onOrderBumpChange?: (checked: boolean) => void;
  checkoutSettings: CheckoutPaymentCheckoutSettings;
  onHostedTipAmountCentsChange?: ({
    tipAmountCents,
  }: {
    tipAmountCents: number;
  }) => void;
  onTipCentsChange?: (tipCents: number) => void;
  hideTipSelector?: boolean;
  tipCents?: number;
}

function CheckoutPaymentOrderAndTips({
  products,
  quantities,
  onQuantityChange,
  onRemoveLine,
  onLineVariantChange,
  total,
  shippingCents = 0,
  shippingLabel = "",
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  orderBumpCents = 0,
  orderBumpSelectable = false,
  orderBumpLineTotalCents = 0,
  orderBumpTitle = "",
  orderBumpDescription,
  orderBumpChecked = false,
  onOrderBumpChange,
  checkoutSettings,
  onHostedTipAmountCentsChange,
  onTipCentsChange,
  hideTipSelector = false,
  tipCents: externalTipCents = 0,
}: CheckoutPaymentOrderAndTipsProps) {
  const [internalTipCents, setInternalTipCents] = useState(0);

  const effectiveTipCents = hideTipSelector ? externalTipCents : internalTipCents;

  const handleInternalTipCentsChange = useCallback(
    ({ tipAmountCents }: { tipAmountCents: number }) => {
      setInternalTipCents(tipAmountCents);
      onHostedTipAmountCentsChange?.({ tipAmountCents });
      onTipCentsChange?.(tipAmountCents);
    },
    [onHostedTipAmountCentsChange, onTipCentsChange],
  );

  return (
    <>
      <OrderQuantitySelector
        products={products}
        quantities={quantities}
        onQuantityChange={onQuantityChange}
        onRemoveLine={onRemoveLine}
        onLineVariantChange={onLineVariantChange}
        total={total}
        shippingCents={shippingCents}
        shippingLabel={shippingLabel || undefined}
        shippingProtectionCents={shippingProtectionCents}
        shippingProtectionSelectable={shippingProtectionSelectable}
        shippingProtectionUnitCents={shippingProtectionUnitCents}
        shippingProtectionChecked={shippingProtectionChecked}
        onShippingProtectionChange={onShippingProtectionChange}
        orderBumpCents={orderBumpCents}
        orderBumpSelectable={orderBumpSelectable}
        orderBumpLineTotalCents={orderBumpLineTotalCents}
        orderBumpTitle={orderBumpTitle}
        orderBumpDescription={orderBumpDescription}
        orderBumpChecked={orderBumpChecked}
        onOrderBumpChange={onOrderBumpChange}
        tipAmount={effectiveTipCents}
        showTipLineItem={hideTipSelector && effectiveTipCents > 0}
        checkoutColors={checkoutSettings?.colors ?? null}
        shippingProtectionAccentBackground
        orderBumpAccentBackground
      />

      {!hideTipSelector && checkoutSettings?.tipping?.enabled ? (
        <CheckoutTipSelector
          tipping={checkoutSettings.tipping}
          colors={checkoutSettings.colors}
          onTipCentsChange={handleInternalTipCentsChange}
        />
      ) : null}
    </>
  );
}

function HostedPaymentElementBlock({
  paymentElementOptions,
  paymentMode,
  name,
  email,
  tipAmountCents,
  onFinalizeHostedCheckout,
  isProcessing,
  error,
  setError,
  setIsProcessing,
  buttonColorHex,
  total,
  shippingCents,
  shippingProtectionCents,
  orderBumpCents = 0,
  checkoutId,
  getCartLinesForInventoryCheck,
  variantSelectionsComplete = true,
  validateVariantsForPayment,
  submitBtnText,
}: {
  paymentElementOptions: { layout: "tabs" };
  paymentMode: "payment" | "setup";
  name: string;
  email: string;
  tipAmountCents: number;
  onFinalizeHostedCheckout?: (args: {
    tipAmountCents?: number;
    paymentIntentId?: string;
    setupIntentId?: string;
  }) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  buttonColorHex: string;
  total: number;
  shippingCents: number;
  shippingProtectionCents: number;
  orderBumpCents?: number;
  checkoutId?: string;
  getCartLinesForInventoryCheck?: () => CheckoutSessionLineItemBody[];
  variantSelectionsComplete?: boolean;
  validateVariantsForPayment?: () =>
    | { ok: true }
    | { ok: false; message?: string };
  submitBtnText?: string;
}) {
  const stripe = useStripe();
  const submitLabel = resolveCheckoutSubmitBtnText(submitBtnText);
  const elements = useElements();
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const handlePay = async () => {
    const variantGate = validateVariantsForPayment?.();
    if (variantGate && !variantGate.ok) {
      setError(variantGate.message ?? "Please choose an option for each product.");
      return;
    }

    if (!stripe || !elements || !paymentElementReady) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? "Please check your payment details.");
        setIsProcessing(false);
        return;
      }

      if (checkoutId) {
        let inventoryUrl = `/api/checkout/${checkoutId}`;
        if (getCartLinesForInventoryCheck) {
          inventoryUrl += `?cart=${encodeURIComponent(
            JSON.stringify(getCartLinesForInventoryCheck())
          )}`;
        }
        const inventoryRes = await fetch(inventoryUrl);
        if (!inventoryRes.ok) {
          const errJson = (await inventoryRes.json().catch(() => ({}))) as {
            error?: string;
          };
          setError(errJson.error ?? "Could not verify inventory.");
          setIsProcessing(false);
          return;
        }
        const inventoryData = (await inventoryRes.json()) as {
          outOfStock?: boolean;
        };
        if (inventoryData.outOfStock === true) {
          setError(
            "One or more items are out of stock. Adjust your cart and try again."
          );
          setIsProcessing(false);
          return;
        }
      }

      const confirmParams = {
        return_url: `${window.location.origin}${window.location.pathname}`,
        payment_method_data: {
          billing_details: {
            name: name.trim() || undefined,
            email: email.trim() || undefined,
          },
        },
      };

      if (paymentMode === "setup") {
        const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams,
          redirect: "if_required",
        });
        if (confirmErr) {
          setError(confirmErr.message ?? "Card setup failed");
          setIsProcessing(false);
          return;
        }
        if (onFinalizeHostedCheckout) {
          await onFinalizeHostedCheckout({
            ...(tipAmountCents > 0 ? { tipAmountCents } : {}),
            ...(setupIntent?.id ? { setupIntentId: setupIntent.id } : {}),
          });
        }
      } else {
        const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: "if_required",
        });
        if (confirmErr) {
          setError(confirmErr.message ?? "Payment failed");
          setIsProcessing(false);
          return;
        }
        if (onFinalizeHostedCheckout) {
          await onFinalizeHostedCheckout({
            ...(tipAmountCents > 0 ? { tipAmountCents } : {}),
            ...(paymentIntent?.id ? { paymentIntentId: paymentIntent.id } : {}),
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsProcessing(false);
    }
  };

  const payableTotal =
    total +
    shippingCents +
    shippingProtectionCents +
    orderBumpCents +
    tipAmountCents;
  const blockZeroTotal =
    validateVariantsForPayment == null
      ? payableTotal === 0
      : variantSelectionsComplete && payableTotal === 0;

  return (
    <>
      <div className="space-y-2">
        <div className="p-3 border border-gray-300 rounded-md">
          <PaymentElement
            options={paymentElementOptions}
            onReady={() => setPaymentElementReady(true)}
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
      <button
        type="button"
        className="w-full px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: buttonColorHex,
          color: getContrastTextColor(buttonColorHex),
        }}
        onClick={() => void handlePay()}
        disabled={
          !stripe ||
          isProcessing ||
          blockZeroTotal ||
          !elements ||
          !paymentElementReady
        }
      >
        {isProcessing ? "Processing..." : submitLabel}
      </button>
    </>
  );
}

export interface HostedCheckoutPaymentProps {
  stripePromise: Promise<Stripe | null>;
  clientSecret: string | null;
  sessionError: string | null;
  elementsKey: string;
  appearance: Appearance;
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  onRemoveLine?: ({ displayIndex }: { displayIndex: number }) => void;
  onLineVariantChange?: ({
    displayIndex,
    variantId,
  }: {
    displayIndex: number;
    variantId: string;
  }) => void;
  total: number;
  shippingCents?: number;
  shippingLabel?: string;
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  orderBumpCents?: number;
  orderBumpSelectable?: boolean;
  orderBumpLineTotalCents?: number;
  orderBumpTitle?: string;
  orderBumpDescription?: string;
  orderBumpChecked?: boolean;
  onOrderBumpChange?: (checked: boolean) => void;
  name: string;
  email: string;
  onHostedTipAmountCentsChange?: ({
    tipAmountCents,
  }: {
    tipAmountCents: number;
  }) => void;
  hideTipSelector?: boolean;
  tipCents?: number;
  onFinalizeHostedCheckout?: (args: {
    tipAmountCents?: number;
    paymentIntentId?: string;
    setupIntentId?: string;
  }) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  checkoutSettings: CheckoutPaymentCheckoutSettings;
  paymentMode?: "payment" | "setup";
  /** With checkoutId, loads checkout before confirm and blocks payment when the API reports outOfStock. */
  checkoutId?: string;
  getCartLinesForInventoryCheck?: () => CheckoutSessionLineItemBody[];
  /** When false, payment session is not created until every variant line has a selection (hosted checkout page). */
  variantSelectionsComplete?: boolean;
  validateVariantsForPayment?: () =>
    | { ok: true }
    | { ok: false; message?: string };
  /** From page checkout action; defaults to "Place Order". */
  submitBtnText?: string;
}

export function HostedCheckoutPayment({
  stripePromise,
  clientSecret,
  sessionError,
  variantSelectionsComplete = true,
  validateVariantsForPayment,
  elementsKey,
  appearance,
  products,
  quantities,
  onQuantityChange,
  onRemoveLine,
  onLineVariantChange,
  total,
  shippingCents = 0,
  shippingLabel = "",
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  orderBumpCents = 0,
  orderBumpSelectable = false,
  orderBumpLineTotalCents = 0,
  orderBumpTitle = "",
  orderBumpDescription,
  orderBumpChecked = false,
  onOrderBumpChange,
  name,
  email,
  onHostedTipAmountCentsChange,
  hideTipSelector = false,
  tipCents = 0,
  onFinalizeHostedCheckout,
  isProcessing,
  error,
  setError,
  setIsProcessing,
  checkoutSettings,
  paymentMode = "payment",
  checkoutId,
  getCartLinesForInventoryCheck,
  submitBtnText,
}: HostedCheckoutPaymentProps) {
  const submitLabel = resolveCheckoutSubmitBtnText(submitBtnText);
  const backgroundColor = checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const textMuted = mutedOnSurfaceHex(backgroundColor);
  const paymentElementOptions = useMemo(
    () => ({
      layout: "tabs" as const,
    }),
    []
  );
  const buttonColorHex = checkoutSettings?.colors?.buttonColor || "#000000";
  const [reportedTipCents, setReportedTipCents] = useState(0);
  const effectiveReportedTipCents = hideTipSelector ? tipCents : reportedTipCents;

  const preparingOrErrorMsg = sessionError?.trim() || null;

  const payableTotalCents =
    total +
    shippingCents +
    shippingProtectionCents +
    orderBumpCents +
    effectiveReportedTipCents;
  const hasPayableTotal = payableTotalCents > 0;

  const awaitingPaymentSession =
    variantSelectionsComplete &&
    !(clientSecret?.trim()) &&
    !preparingOrErrorMsg;

  const elementsOptions = useMemo(() => {
    const secret = clientSecret?.trim();
    if (!secret) return null;
    return { clientSecret: secret, appearance };
  }, [clientSecret, appearance]);

  const elementsOptionsRef = useRef(elementsOptions);
  const elementsKeyRef = useRef(elementsKey);
  if (!isProcessing) {
    elementsOptionsRef.current = elementsOptions;
    elementsKeyRef.current = elementsKey;
  }
  const activeElementsOptions = isProcessing
    ? elementsOptionsRef.current
    : elementsOptions;
  const activeElementsKey = isProcessing ? elementsKeyRef.current : elementsKey;

  return (
    <div className="space-y-4 flex-1">
      <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
        <CheckoutPaymentOrderAndTips
          products={products}
          quantities={quantities}
          onQuantityChange={onQuantityChange}
          onRemoveLine={onRemoveLine}
          onLineVariantChange={onLineVariantChange}
          total={total}
          shippingCents={shippingCents}
          shippingLabel={shippingLabel}
          shippingProtectionCents={shippingProtectionCents}
          shippingProtectionSelectable={shippingProtectionSelectable}
          shippingProtectionUnitCents={shippingProtectionUnitCents}
          shippingProtectionChecked={shippingProtectionChecked}
          onShippingProtectionChange={onShippingProtectionChange}
          orderBumpCents={orderBumpCents}
          orderBumpSelectable={orderBumpSelectable}
          orderBumpLineTotalCents={orderBumpLineTotalCents}
          orderBumpTitle={orderBumpTitle}
          orderBumpDescription={orderBumpDescription}
          orderBumpChecked={orderBumpChecked}
          onOrderBumpChange={onOrderBumpChange}
          checkoutSettings={checkoutSettings}
          onHostedTipAmountCentsChange={onHostedTipAmountCentsChange}
          onTipCentsChange={setReportedTipCents}
          hideTipSelector={hideTipSelector}
          tipCents={tipCents}
        />

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            {!clientSecret ? (
              <div className="p-3 border border-gray-300 rounded-md min-h-[140px] flex items-center justify-center px-3">
                {!variantSelectionsComplete ? (
                  <p className="text-sm text-center" style={{ color: textMuted }}>
                    Choose an option for each product above to continue.
                  </p>
                ) : preparingOrErrorMsg ? (
                  <p className="text-sm text-red-500 text-center">{preparingOrErrorMsg}</p>
                ) : (
                  <p className="text-sm text-center" style={{ color: textMuted }}>
                    {paymentMode === "setup"
                      ? "Preparing card setup…"
                      : "Preparing payment…"}
                  </p>
                )}
              </div>
            ) : activeElementsOptions ? (
              <Elements
                key={activeElementsKey}
                stripe={stripePromise}
                options={activeElementsOptions}
              >
                <HostedPaymentElementBlock
                  paymentElementOptions={paymentElementOptions}
                  paymentMode={paymentMode}
                  name={name}
                  email={email}
                  tipAmountCents={effectiveReportedTipCents}
                  onFinalizeHostedCheckout={onFinalizeHostedCheckout}
                  isProcessing={isProcessing}
                  error={error}
                  setError={setError}
                  setIsProcessing={setIsProcessing}
                  buttonColorHex={buttonColorHex}
                  total={total}
                  shippingCents={shippingCents}
                  shippingProtectionCents={shippingProtectionCents}
                  orderBumpCents={orderBumpCents}
                  checkoutId={checkoutId}
                  getCartLinesForInventoryCheck={getCartLinesForInventoryCheck}
                  variantSelectionsComplete={variantSelectionsComplete}
                  validateVariantsForPayment={validateVariantsForPayment}
                  submitBtnText={submitBtnText}
                />
              </Elements>
            ) : null}
          </div>
        </div>

        {!clientSecret && error ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : null}

        {!clientSecret ? (
          <button
            type="button"
            className="w-full px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: buttonColorHex,
              color: getContrastTextColor(buttonColorHex),
            }}
            disabled={
              !hasPayableTotal ||
              awaitingPaymentSession ||
              Boolean(preparingOrErrorMsg)
            }
            onClick={() => {
              const r = validateVariantsForPayment?.();
              if (r && !r.ok) {
                setError(
                  r.message ?? "Please choose an option for each product."
                );
                return;
              }
              setError(null);
            }}
          >
            {submitLabel}
          </button>
        ) : null}
      </form>
    </div>
  );
}
